/**
 * lib/env.ts — VEXL Nova Environment Validation
 *
 * Validates all required VITE_* env variables at startup using Zod.
 * If any variable is missing or malformed, the app throws immediately
 * with a clear, actionable error — no cryptic runtime failures later.
 *
 * Usage:
 *   import { env } from '@/lib/env';
 *   const url = env.VITE_SUPABASE_URL;
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Schema
// ─────────────────────────────────────────────────────────────────────────────

const envSchema = z.object({
  /** Supabase project URL — required for Auth + DB */
  VITE_SUPABASE_URL: z
    .string({ required_error: 'VITE_SUPABASE_URL is required' })
    .url('VITE_SUPABASE_URL must be a valid URL (e.g. https://xyz.supabase.co)'),

  /** Supabase anonymous key — used for client-side queries */
  VITE_SUPABASE_ANON_KEY: z
    .string({ required_error: 'VITE_SUPABASE_ANON_KEY is required' })
    .min(30, 'VITE_SUPABASE_ANON_KEY appears too short — check your Supabase project settings'),

  /** Node.js API proxy base URL — defaults to local dev server */
  VITE_API_BASE_URL: z
    .string()
    .url()
    .optional()
    .default('http://127.0.0.1:3001'),

  /** Sentry project DSN for error tracking */
  VITE_SENTRY_DSN: z
    .string()
    .optional(),

  /** PostHog api key for product analytics */
  VITE_POSTHOG_KEY: z
    .string()
    .optional(),

  /** PostHog host URL — defaults to global US ingest endpoint */
  VITE_POSTHOG_HOST: z
    .string()
    .url()
    .optional()
    .default('https://us.i.posthog.com'),

  /** Alias for PostHog token from official docs */
  VITE_PUBLIC_POSTHOG_PROJECT_TOKEN: z
    .string()
    .optional(),

  /** Alias for PostHog host from official docs */
  VITE_PUBLIC_POSTHOG_HOST: z
    .string()
    .url()
    .optional(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Parse & Export
// ─────────────────────────────────────────────────────────────────────────────

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((i) => `  • ${i.path.join('.')}: ${i.message}`)
    .join('\n');

  // Log in a box so it's instantly visible in the terminal
  console.error(
    `\n╔══════════════════════════════════════════════════════════╗\n` +
    `║  ❌  VEXL Nova — Missing / Invalid Environment Variables  ║\n` +
    `╠══════════════════════════════════════════════════════════╣\n` +
    `${issues}\n` +
    `╠══════════════════════════════════════════════════════════╣\n` +
    `║  → Copy .env.example to .env.local and fill in values    ║\n` +
    `╚══════════════════════════════════════════════════════════╝\n`
  );

  throw new Error(
    `[VEXL Nova] App cannot start — invalid environment configuration.\n${issues}`
  );
}

/** Fully typed, validated environment variables for VEXL Nova */
export const env = parsed.data;
export type Env = z.infer<typeof envSchema>;
